import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import SvgIcon from "../../../../parts/SvgIcon";
import { initials } from "../door-access-utils";

const DEBOUNCE_MS = 250;

/** Member search with autocomplete over the gym's members that have door events. */
const MemberFilter = ({ api, member, onSelect }) => {
  const { t } = useTranslation("coaching-portal");
  const [text, setText] = useState(member?.name || "");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [searching, setSearching] = useState(false);
  const box = useRef(null);

  useEffect(() => {
    setText(member?.name || "");
  }, [member]);

  useEffect(() => {
    const q = text.trim();
    if (!open || !q || q === member?.name) {
      setResults([]);
      setSearched(false);
      setSearching(false);
      return undefined;
    }
    let cancelled = false;
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const res = await api("door-members-search", { query: { q } });
        const data = res.ok ? await res.json() : { members: [] };
        if (cancelled) return;
        setResults(data.members || []);
        setSearched(true);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [api, text, open, member]);

  useEffect(() => {
    const close = (event) => {
      if (box.current && !box.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const choose = (picked) => {
    setOpen(false);
    onSelect({ id: picked.id, name: picked.name });
  };

  return (
    <div className="door-member-filter" ref={box}>
      <label className="door-search">
        <SvgIcon src="/img/new-icons/search.svg" useCurrentColor />
        <input
          type="text"
          autoComplete="off"
          placeholder={t("doorAccess.filters.searchMember")}
          aria-label={t("doorAccess.filters.searchMember")}
          value={text}
          onFocus={() => setOpen(true)}
          onChange={(event) => {
            setText(event.target.value);
            setOpen(true);
          }}
        />
      </label>
      {open && searching && (
        <div className="door-menu" aria-busy="true">
          <div className="door-menu__skeleton" aria-hidden="true">
            <span className="door-skeleton" />
            <span className="door-skeleton" />
            <span className="door-skeleton" />
          </div>
        </div>
      )}
      {open && !searching && searched && (
        <div className="door-menu" role="listbox">
          {results.length === 0 ? (
            <span className="door-menu__empty">
              {t("doorAccess.filters.noMemberMatch", { q: text.trim() })}
            </span>
          ) : (
            results.map((result) => (
              <button
                key={result.id}
                type="button"
                role="option"
                aria-selected={member?.id === result.id}
                onClick={() => choose(result)}
              >
                <span className="door-avatar door-avatar--sm">
                  {initials(result.name)}
                </span>
                {result.name}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default MemberFilter;
